import type { FinanceContact, ComplianceProfileDto } from '@signalvote/shared';
import { ContactType } from '@signalvote/shared';

export type TrafficStatus = 'Red' | 'Yellow' | 'Green';

export const LIMIT_WARNING_THRESHOLD_PCT = 70;
export const IRS_1099_THRESHOLD = 600;

// KNOWN UN-MIGRATED READER of the donor-info rule (tracked cutover item). The backend
// Donor-info verdict is the single source of truth for "which required identity fields is
// this itemized donor missing," and its answer already reaches the client as the
// verdict-derived `missingItemizedFields` on the contact DTO. This helper still recomputes
// the employer-required signal client-side (via `contact.missingRequiredInfo`, itself
// a client-side recomputation), so it can drift from the verdict. Do NOT extend this local
// computation; the cutover is to derive the Red "missing required info" branch from the
// backend verdict signal instead of recomputing it here.
export function getTrafficStatus(
  contact: FinanceContact,
  profile: ComplianceProfileDto,
): TrafficStatus {
  if (contact.type === ContactType.Payee) return 'Green';

  // Red: missing required info AND over itemization threshold
  if (
    contact.missingRequiredInfo &&
    contact.calendarYearTotal > (profile.itemizationThreshold ?? 0)
  ) {
    return 'Red';
  }

  const limit = profile.individualLimitPerElection;
  if (limit !== null) {
    if (profile.perElectionTracking) {
      if (contact.primaryTotal > limit || contact.generalTotal > limit) return 'Red';
      const primaryPct = (contact.primaryTotal / limit) * 100;
      const generalPct = (contact.generalTotal / limit) * 100;
      if (primaryPct >= LIMIT_WARNING_THRESHOLD_PCT || generalPct >= LIMIT_WARNING_THRESHOLD_PCT) {
        return 'Yellow';
      }
    } else {
      if (contact.calendarYearTotal > limit) return 'Red';
      if ((contact.calendarYearTotal / limit) * 100 >= LIMIT_WARNING_THRESHOLD_PCT) {
        return 'Yellow';
      }
    }
  }

  return 'Green';
}

export function trafficStatusToCss(status: TrafficStatus): string {
  switch (status) {
    case 'Red': return 'bg-red-500';
    case 'Yellow': return 'bg-yellow-500';
    case 'Green': return 'bg-green-500';
  }
}

export function trafficStatusPriority(status: TrafficStatus): number {
  switch (status) {
    case 'Red': return 0;
    case 'Yellow': return 1;
    case 'Green': return 2;
  }
}
